import React, { useState, useEffect } from 'react';
import { KatButton, KatInput } from '@amzn/katal-react';
import { CORAInput, MODE_SELECTORS, FULFILLMENT_PROGRAMS } from 'src/features/types/coraTypes';

interface CORAFormProps {
    initialInput: CORAInput;
    onSubmit?: (input: CORAInput) => void;
    isLoading?: boolean;
}

const CORAForm: React.FC<CORAFormProps> = ({ initialInput, onSubmit, isLoading = false }) => {
    const [input, setInput] = useState<CORAInput>(initialInput);

    useEffect(() => {
        setInput(initialInput);
    }, [initialInput]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!input.nodeId || input.nodeId.trim().length === 0) {
            return;
        }

        let nodeIds: string[] = [];
        if (input.nodeId) {
            nodeIds = [input.nodeId];
        } else if (input.nodeIds) {
            nodeIds = input.nodeIds;
        }

        const submissionInput: CORAInput = {
            ...input,
            nodeIds
        };

        onSubmit?.(submissionInput);
    };

    const handleNodeIdChange = (value: string) => {
        const nodeIds = value.split(',').map(id => id.trim()).filter(id => id.length > 0);
        setInput({
            ...input,
            nodeId: value,
            nodeIds
        });
    };

    return (
        <form onSubmit={handleSubmit} className="cora-compact-form">
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'end', flexWrap: 'wrap' }}>
                <div style={{ minWidth: '200px' }}>
                    <label htmlFor="nodeId" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 'bold' }}>
                        Node ID(s) <span style={{ color: 'red' }}>*</span>
                    </label>
                    <KatInput
                        id="nodeId"
                        type="text"
                        value={input.nodeId || ''}
                        onChange={(e: any) => {
                            const value = e.target?.value || e.value || '';
                            handleNodeIdChange(value);
                        }}
                        placeholder="e.g., BFI4"
                        required
                        className="cora-form-input"
                    />
                </div>

                <div style={{ minWidth: '120px' }}>
                    <label htmlFor="duration" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 'bold' }}>
                        Duration
                    </label>
                    <select
                        id="duration"
                        value={input.durationOfRiskWindowInDays.toString()}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setInput({ ...input, durationOfRiskWindowInDays: Number(e.target.value) })}
                        required
                        style={{
                            width: '100%',
                            padding: '0.4rem',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                        }}
                    >
                        <option value="1">24 hours</option>
                        <option value="7">7 days</option>
                        <option value="14">14 days</option>
                        <option value="21">21 days</option>
                    </select>
                </div>

                <div style={{ minWidth: '150px' }}>
                    <label htmlFor="modeSelector" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 'bold' }}>
                        Mode Selector
                    </label>
                    <select
                        id="modeSelector"
                        value={input.modeSelector}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setInput({ ...input, modeSelector: e.target.value })}
                        required
                        style={{
                            width: '100%',
                            padding: '0.4rem',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                        }}
                    >
                        <option value={MODE_SELECTORS.EXSD}>EXSD</option>
                        <option value={MODE_SELECTORS.PROCESS_PATH_FAMILY}>PPF</option>
                    </select>
                </div>

                <div style={{ minWidth: '180px' }}>
                    <label htmlFor="fulfillmentProgram" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 'bold' }}>
                        Fulfillment Program
                    </label>
                    <select
                        id="fulfillmentProgram"
                        value={input.fulfillmentProgram}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setInput({ ...input, fulfillmentProgram: e.target.value })}
                        required
                        style={{
                            width: '100%',
                            padding: '0.4rem',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                        }}
                    >
                        <option value={FULFILLMENT_PROGRAMS.AMAZON_FULFILLMENT}>AMAZON_FULFILLMENT</option>
                        <option value={FULFILLMENT_PROGRAMS.DIRECT_FULFILLMENT}>DIRECT_FULFILLMENT</option>
                        <option value={FULFILLMENT_PROGRAMS.EF_FORWARD_DEPLOY}>EF_FORWARD_DEPLOY</option>
                        <option value={FULFILLMENT_PROGRAMS.EF_HEAVY_BULKY}>EF_HEAVY_BULKY</option>
                        <option value={FULFILLMENT_PROGRAMS.EF_MINI_FC}>EF_MINI_FC</option>
                        <option value={FULFILLMENT_PROGRAMS.EF_MULTI_SELLER_FLEX}>EF_MULTI_SELLER_FLEX</option>
                        <option value={FULFILLMENT_PROGRAMS.EF_SELLER_FLEX}>EF_SELLER_FLEX</option>
                        <option value={FULFILLMENT_PROGRAMS.EF_SUB_SAME_DAY}>EF_SUB_SAME_DAY</option>
                        <option value={FULFILLMENT_PROGRAMS.EF_THIRD_PARTY_LOGISTICS}>EF_THIRD_PARTY_LOGISTICS</option>
                        <option value={FULFILLMENT_PROGRAMS.EF_VENDOR_FLEX}>EF_VENDOR_FLEX</option>
                        <option value={FULFILLMENT_PROGRAMS.EF_SMART_CONNECT}>EF_SMART_CONNECT</option>
                    </select>
                </div>

                <div>
                    <KatButton
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                        className="cora-form-button"
                    >
                        {isLoading ? 'Loading...' : 'Update'}
                    </KatButton>
                </div>
            </div>
        </form>
    );
};

export default CORAForm;
